'use client';
import React from 'react';
import Link from 'next/link';
import {
  ShoppingBag, TrendingUp, Award, Landmark, BarChart3,
  Wheat, BookOpen, Users, ShieldCheck, ArrowRight
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const modules = [
  {
    id: 'mod-marketplace',
    icon: ShoppingBag,
    title: 'Global Halal Marketplace',
    description: 'Connect with verified halal suppliers and buyers across 42+ countries with certified product listings.',
    href: '/global-halal-marketplace',
    color: 'var(--emerald-bright)',
  },
  {
    id: 'mod-investment',
    icon: TrendingUp,
    title: 'Halal Investment Exchange',
    description: 'Discover Shariah-compliant investment opportunities with full due diligence documentation.',
    href: '/halal-investment-exchange',
    color: 'var(--gold)',
  },
  {
    id: 'mod-certification',
    icon: Award,
    title: 'Digital Certification Registry',
    description: 'Searchable halal certificate registry with QR verification and blockchain-style audit trails.',
    href: '/digital-certification-registry',
    color: 'var(--info)',
  },
  {
    id: 'mod-finance',
    icon: Landmark,
    title: 'Islamic Finance & Takaful',
    description: 'Match SMEs with Murabahah, Tawarruq, Sukuk, and Takaful financing partners instantly.',
    href: '/main-dashboard',
    color: '#A855F7',
  },
  {
    id: 'mod-intelligence',
    icon: BarChart3,
    title: 'Trade Intelligence & Data',
    description: 'Real-time halal trade analytics, corridor mapping, and market opportunity intelligence.',
    href: '/main-dashboard',
    color: 'var(--warning)',
  },
  {
    id: 'mod-agriculture',
    icon: Wheat,
    title: 'Food Security & Agriculture',
    description: 'Halal-certified agricultural supply chains connecting OIC markets with verified producers.',
    href: '/main-dashboard',
    color: '#22C55E',
  },
  {
    id: 'mod-consulting',
    icon: BookOpen,
    title: 'Business Consulting & Analytics',
    description: 'Strategic advisory services for halal market entry, compliance, and growth planning.',
    href: '/main-dashboard',
    color: 'var(--emerald-bright)',
  },
  {
    id: 'mod-expo',
    icon: Users,
    title: 'Halal Expo & Business Matching',
    description: 'AI-powered B2B matching for expos, trade missions, and investment meetings.',
    href: '/main-dashboard',
    color: 'var(--gold)',
  },
  {
    id: 'mod-compliance',
    icon: ShieldCheck,
    title: 'Compliance & Trust Engine',
    description: 'Institutional-grade compliance verification, Shariah review workflow, and trust scoring.',
    href: '/main-dashboard',
    color: 'var(--info)',
  },
];

export default function EcosystemModulesSection() {
  return (
    <section className="py-24" style={{ background: 'var(--background)' }}>
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16">
        <div className="text-center mb-16">
          <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: 'var(--gold)', letterSpacing: '0.1em' }}>
            Ecosystem Modules
          </p>
          <h2 className="text-4xl xl:text-5xl font-bold mb-4">
            One Platform. Nine Pillars.
          </h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: 'var(--muted-foreground)' }}>
            Every dimension of the halal economy — unified in a single trusted digital infrastructure.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-6">
          {modules?.map((mod) => {
            const Icon = mod?.icon;
            return (
              <div
                key={mod?.id}
                className="rounded-2xl p-6 shadow-card transition-all duration-300 hover:-translate-y-1 group flex flex-col"
                style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 shrink-0"
                  style={{ background: `${mod?.color}15`, border: `1px solid ${mod?.color}30` }}
                >
                  <Icon size={22} style={{ color: mod?.color }} />
                </div>
                <h3 className="text-base font-semibold mb-2">{mod?.title}</h3>
                <p className="text-sm flex-1 leading-relaxed mb-4" style={{ color: 'var(--muted-foreground)' }}>
                  {mod?.description}
                </p>
                <Link
                  href={mod?.href}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold transition-all duration-200 group-hover:gap-2.5"
                  style={{ color: mod?.color }}
                >
                  Open Module
                  <ArrowRight size={14} />
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}